import { Colors } from '@oy/uicore';
import {
  Button,
  ButtonWithIcon,
  ConfirmationModal,
  IconButton,
  Message,
  Steppers,
  Text,
} from '@oy/uidesktop';
import { useTranslate } from 'src/hooks';
import styles from './AddChild.module.css';
import { ChildTypeStep } from './Steps/ChildTypeStep';
import { RegisterChildStep } from './Steps/RegisterChildStep';
import { AddChildModalState, AddChildStep, STEP_OPTIONS } from './constants';
import { useAddChildImpl } from './useAddChildImpl';

export const AddChild = () => {
  const { getTranslation } = useTranslate();
  const {
    onClickConfirmConnect,
    onClickNext,
    onClose,
    onCloseConfirmationModal,
    onSubmitRegisterChild,
    setFormState,
    setStep,
    state,
  } = useAddChildImpl();
  const { disableNext, modalState, form, isProdEnv, step } = state;

  const sideText =
    step === AddChildStep.ChildType
      ? 'multiEntity_sideText_childType'
      : 'multiEntity_sideText_register';

  return (
    <div className={styles.createContainer}>
      <div className={styles.header}>
        <IconButton
          color={IconButton.Color.Secondary}
          size={IconButton.Size.Medium}
          iconName="close"
          id="btnClose"
          onClick={onClose}
        />
        <div style={{ width: 1, backgroundColor: '#D2D5D7', height: 32 }} />
        <Text color={Colors.BLACK100} textStyle={Text.Style.BodyXlSemibold}>
          {getTranslation('children_listPage_addChild_label')}
        </Text>
      </div>
      {isProdEnv && (
        <div className={styles.stepperContainer}>
          <Steppers
            direction={Steppers.Direction.HORIZONTAL}
            activeIndex={step}
            steps={STEP_OPTIONS.map(step => ({
              ...step,
              label: getTranslation(step.label),
            }))}
            type={Steppers.Type.LINEAR}
            variant={Steppers.Variant.MINIMAL}
          />
        </div>
      )}

      <div className={styles.mainContainer}>
        <div className={styles.formContainer}>
          {step === AddChildStep.ChildType && (
            <ChildTypeStep formState={form} setFormState={setFormState} />
          )}
          {step === AddChildStep.RegisterChild && (
            <RegisterChildStep
              initialUsername={form.username!}
              handleSubmitRegister={onSubmitRegisterChild}
            />
          )}
          {!isProdEnv && (
            <Message
              showIcon
              type={Message.Type.Warning}
              className={styles.message}
            >
              {getTranslation('MAMdemo_generateChild_message')}
            </Message>
          )}
        </div>
        <div className={styles.infoContainer}>
          <div className={styles.infoItem}>
            <Text textStyle={Text.Style.BodyMdRegular}>
              {getTranslation(sideText)}
            </Text>
          </div>
        </div>
      </div>

      <div className={styles.buttonContainer}>
        {step !== 0 && (
          <ButtonWithIcon
            buttonType={ButtonWithIcon.Type.Text}
            iconName="back"
            onClick={() => setStep(prev => prev - 1)}
            color={ButtonWithIcon.Color.Secondary}
            className={styles.backButton}
            id="btnBack"
            type="button"
          >
            {getTranslation('common_back')}
          </ButtonWithIcon>
        )}
        <Button
          size={Button.Size.Medium}
          type="submit"
          form="add_child"
          className={styles.actionButton}
          onClick={onClickNext}
          disabled={disableNext && isProdEnv}
        >
          {getTranslation(
            isProdEnv ? 'common_next' : 'MAMdemo_generateChild_button',
          )}
        </Button>
      </div>

      <ConfirmationModal
        show={modalState === AddChildModalState.ConfirmConnect}
        onSecondaryButtonClick={onCloseConfirmationModal}
        onPrimaryButtonClick={onClickConfirmConnect}
        title={`${getTranslation(
          'children_registerChild_usernameExist_modalTitle',
        )} ${form.username}`}
        primaryButtonName={getTranslation('common_connect')}
        secondaryButtonName={getTranslation('common_status_cancel')}
      >
        <Text textStyle={Text.Style.BodyLgRegular}>
          {`Username '${form.username}' ${getTranslation(
            'children_registerChild_usernameExist_description',
          )}`}
        </Text>
      </ConfirmationModal>

      <ConfirmationModal
        show={modalState === AddChildModalState.RequestAlreadySent}
        icon="red-exclamation"
        onSecondaryButtonClick={onCloseConfirmationModal}
        onPrimaryButtonClick={onCloseConfirmationModal}
        title={getTranslation(
          'children_registerChild_alreadySentConnectRequest',
        )}
        description={`
         ${getTranslation(
           'children_registerChild_alreadySentConnectRequestWith',
         )} \`${form.username}\`. ${getTranslation(
           'children_registerChild_pleaseWait',
         )}.`}
        primaryButtonName={getTranslation('common_changeUsername')}
        secondaryButtonName={getTranslation('common_status_cancel')}
      >
        <Text textStyle={Text.Style.BodyLgRegular}>
          {`
         ${getTranslation(
           'children_registerChild_alreadySentConnectRequestWith',
         )} \`${form.username}\`. ${getTranslation(
           'children_registerChild_pleaseWait',
         )}.`}
        </Text>
      </ConfirmationModal>

      <ConfirmationModal
        show={modalState === AddChildModalState.AlreadyHasRelation}
        icon="red-exclamation"
        onSecondaryButtonClick={onCloseConfirmationModal}
        onPrimaryButtonClick={onCloseConfirmationModal}
        title={getTranslation(
          'children_registerChild_alreadySentConnectRequest',
        )}
        primaryButtonName={getTranslation('common_changeUsername')}
        secondaryButtonName={getTranslation('common_status_cancel')}
      >
        <Text textStyle={Text.Style.BodyLgRegular}>
          {`Username 
        \`${form.username}\` ${getTranslation(
          'children_registerChild_hasBecomeChild_description',
        )}`}
        </Text>
      </ConfirmationModal>

      <ConfirmationModal
        show={modalState === AddChildModalState.ChildAlreadyGenerated}
        title={getTranslation('MAMdemo_canNotAddChild_title')}
        description={getTranslation('MAMdemo_canNotAddChild_description')}
        primaryButtonName={getTranslation('common_understand')}
        onPrimaryButtonClick={onCloseConfirmationModal}
      />
    </div>
  );
};
